<template>
    <section class="portfolio-section">
        <div class="container">
            <!-- Estado de carga -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Cargando portafolio...</p>
            </div>

            <!-- Estado de error -->
            <div v-else-if="error" class="error-state">
                <p>{{ error }}</p>
                <button @click="loadData" class="retry-btn">Reintentar</button>
            </div>

            <!-- Contenido principal -->
            <div v-else>
                <!-- Filtros -->
                <div class="portfolio-filters">
                    <button 
                        v-for="category in categories" 
                        :key="category"
                        @click="filterByCategory(category)"
                        :class="{ 'active': selectedCategory === category }"
                        class="filter-btn"
                    >
                        {{ category }}
                    </button>
                </div>

                <!-- Grid de proyectos -->
                <div class="portfolio-grid">
                    <div 
                        v-for="project in filteredProjects" 
                        :key="project.id"
                        class="portfolio-item"
                        @click="openProjectDetail(project)"
                    >
                        <div class="project-image">
                            <img :src="project.image_url" :alt="project.title" />
                            <div class="project-overlay">
                                <div class="overlay-content">
                                    <h3>{{ project.title }}</h3>
                                    <p>{{ project.subtitle }}</p>
                                    <div class="project-tags">
                                        <span v-for="tag in getProjectTags(project).slice(0, 3)" :key="tag" class="tag">
                                            {{ tag }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="project-info">
                            <h3>{{ project.title }}</h3>
                            <p class="project-subtitle">{{ project.subtitle }}</p>
                            <div class="project-meta">
                                <span class="category" :style="{ backgroundColor: project.category_color + '20', color: project.category_color }">
                                    {{ project.category_name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal de detalle del proyecto -->
            <div v-if="selectedProject" class="project-modal" @click="closeProjectDetail">
                <div class="modal-content" @click.stop>
                    <button class="close-btn" @click="closeProjectDetail">&times;</button>
                    <div class="modal-body">
                        <div class="project-header">
                            <img :src="selectedProject.image_url" :alt="selectedProject.title" class="project-hero-image" />
                            <div class="project-header-info">
                                <h2>{{ selectedProject.title }}</h2>
                                <p class="project-subtitle">{{ selectedProject.subtitle }}</p>
                                <div class="project-meta-detail">
                                    <div class="meta-item">
                                        <strong>Cliente:</strong> {{ selectedProject.client }}
                                    </div>

                                    <div class="meta-item">
                                        <strong>Categoría:</strong> 
                                        <span :style="{ color: selectedProject.category_color }">
                                            {{ selectedProject.category_name }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedProject.description" class="project-description">
                            <h3>Descripción del Proyecto</h3>
                            <p>{{ selectedProject.description }}</p>
                        </div>

                        <div v-if="getTechnologies().length > 0" class="project-technologies">
                            <h3>Tecnologías Utilizadas</h3>
                            <div class="tech-tags">
                                <span v-for="tech in getTechnologies()" :key="tech" class="tech-tag">
                                    {{ tech }}
                                </span>
                            </div>
                        </div>

                        <div v-if="getResults().length > 0" class="project-results">
                            <h3>Resultados Obtenidos</h3>
                            <div class="results-grid">
                                <div v-for="(value, key) in getResults()" :key="key" class="result-item">
                                    <span class="result-label">{{ formatResultLabel(key) }}</span>
                                    <span class="result-value">{{ value }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-if="getGallery().length > 0" class="project-gallery">
                            <h3>Galería del Proyecto</h3>
                            <div class="gallery-grid">
                                <div v-for="(media, index) in getGallery()" :key="index" class="gallery-item">
                                    <img v-if="media.type === 'image'" :src="media.url" :alt="media.alt" />
                                    <video v-else-if="media.type === 'video'" :src="media.url" controls :poster="media.thumbnail"></video>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import apiService from '../config/api.js';

export default {
    name: 'PortfolioComponent',
    data() {
        return {
            projects: [],
            categories: [],
            selectedCategory: 'Todos',
            selectedProject: null,
            loading: true,
            error: null
        }
    },
    computed: {
        filteredProjects() {
            if (this.selectedCategory === 'Todos') {
                return this.projects;
            }
            return this.projects.filter(project => project.category_name === this.selectedCategory);
        }
    },
    async mounted() {
        await this.loadData();
    },
    methods: {
        async loadData() {
            try {
                this.loading = true;
                this.error = null;
                
                console.log('🚀 Iniciando carga de datos del portafolio...');
                console.log('📍 URL de la API:', apiService.baseURL);
                
                // Cargar proyectos y categorías en paralelo
                const [projectsResponse, categoriesResponse] = await Promise.all([
                    apiService.getProjects(),
                    apiService.getCategories()
                ]);
                
                console.log('📊 Respuesta de proyectos:', projectsResponse);
                console.log('📋 Respuesta de categorías:', categoriesResponse);
                
                if (projectsResponse && projectsResponse.success) {
                    this.projects = projectsResponse.data || [];
                    console.log('Proyectos cargados:', this.projects.length);
                } else {
                    const errorMsg = projectsResponse?.error || 'Error al cargar proyectos';
                    console.error('Error en respuesta de proyectos:', projectsResponse);
                    throw new Error(errorMsg);
                }
                
                if (categoriesResponse && categoriesResponse.success) {
                    // Agregar "Todos" al inicio de las categorías
                    this.categories = ['Todos', ...(categoriesResponse.data || []).map(cat => cat.name)];
                    console.log('Categorías cargadas:', this.categories);
                } else {
                    const errorMsg = categoriesResponse?.error || 'Error al cargar categorías';
                    console.error('Error en respuesta de categorías:', categoriesResponse);
                    throw new Error(errorMsg);
                }
                
            } catch (error) {
                console.error('Error cargando datos:', error);
                this.error = `Error al cargar los datos del portafolio: ${error.message}`;
            } finally {
                this.loading = false;
            }
        },
        
        filterByCategory(category) {
            this.selectedCategory = category;
        },
        
        openProjectDetail(project) {
            this.selectedProject = project;
            document.body.style.overflow = 'hidden';
        },
        
        closeProjectDetail() {
            this.selectedProject = null;
            document.body.style.overflow = 'auto';
        },
        
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        
        getTechnologies() {
            try {
                if (!this.selectedProject.technologies) return [];
                const techs = JSON.parse(this.selectedProject.technologies);
                return Array.isArray(techs) ? techs : [];
            } catch (error) {
                console.warn('Error parsing technologies:', error);
                return [];
            }
        },
        
        getResults() {
            try {
                if (!this.selectedProject.results) return {};
                const results = JSON.parse(this.selectedProject.results);
                return typeof results === 'object' && results !== null ? results : {};
            } catch (error) {
                console.warn('Error parsing results:', error);
                return {};
            }
        },
        
        getGallery() {
            try {
                if (!this.selectedProject.gallery) return [];
                const gallery = JSON.parse(this.selectedProject.gallery);
                return Array.isArray(gallery) ? gallery : [];
            } catch (error) {
                console.warn('Error parsing gallery:', error);
                return [];
            }
        },
        
        getProjectTags(project) {
            try {
                if (!project.tags) return [];
                const tags = JSON.parse(project.tags);
                return Array.isArray(tags) ? tags : [];
            } catch (error) {
                console.warn('Error parsing project tags:', error);
                return [];
            }
        },
        
        formatResultLabel(key) {
            const labels = {
                conversionRate: 'Tasa de Conversión',
                trafficIncrease: 'Incremento de Tráfico',
                leadsGenerated: 'Leads Generados',
                reachIncrease: 'Incremento de Alcance',
                engagementRate: 'Tasa de Engagement',
                roi: 'ROI',
                brandRecognition: 'Reconocimiento de Marca',
                customerTrust: 'Confianza del Cliente',
                marketPosition: 'Posición en el Mercado',
                viewCount: 'Visualizaciones',
                salesIncrease: 'Incremento de Ventas',
                monthlySales: 'Ventas Mensuales',
                customerSatisfaction: 'Satisfacción del Cliente',
                followersGrowth: 'Crecimiento de Seguidores'
            };
            return labels[key] || key;
        }
    }
}
</script>

<style lang="scss" scoped>
.portfolio-section {
    font-family: 'Poppins', sans-serif;
    padding: 0 0 80px 0;
    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
    }

    // Estados de carga y error
    .loading-state,
    .error-state {
        text-align: center;
        padding: 60px 20px;
        
        p {
            font-size: 18px;
            color: #666;
            margin-top: 20px;
        }
    }

    .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #1f8cc6;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .retry-btn {
        background: #1f8cc6;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
        margin-top: 20px;
        transition: background-color 0.3s ease;

        &:hover {
            background: #1673a3;
        }
    }

    .portfolio-filters {
        font-family: 'Nunito', sans-serif;
        display: flex;
        justify-content: center;
        gap: 30px;
        margin-bottom: 20px;
        overflow-x: auto;
        white-space: nowrap;
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
        }
        @media (max-width: 425px) {
            justify-content: flex-start;
        }

        .filter-btn {
            background: none;
            border: none;
            padding: 12px 24px;
            font-size: 16px;
            font-weight: 500;
            color: #666;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;

            &:hover {
                color: #000000;
            }

            &.active {
                color: #141414;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: 5px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 30px;
                    height: 3px;
                    background: #ffd700;
                    border-radius: 2px;
                }
            }
        }
    }

    .portfolio-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 30px;

        @media (max-width: 1200px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 700px) {
            max-width: 400px;
            margin: 0 auto;
            grid-template-columns: repeat(1, 1fr);
        }

        .portfolio-item {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
            cursor: pointer;

            &:hover {
                transform: translateY(-5px);
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

                .project-overlay {
                    opacity: 1;
                }
            }

            .project-image {
                position: relative;
                height: 250px;
                overflow: hidden;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.3s ease;
                }

                .project-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(31, 140, 198, 0.9);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;

                    .overlay-content {
                        text-align: center;
                        color: white;
                        padding: 20px;

                        h3 {
                            font-size: 20px;
                            margin-bottom: 10px;
                            font-weight: 600;
                        }

                        p {
                            font-size: 14px;
                            margin-bottom: 15px;
                            opacity: 0.9;
                        }

                        .project-tags {
                            display: flex;
                            gap: 8px;
                            justify-content: center;
                            flex-wrap: wrap;

                            .tag {
                                background: rgba(255, 255, 255, 0.2);
                                padding: 4px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                            }
                        }
                    }
                }
            }

            .project-info {
                padding: 20px;

                h3 {
                    font-size: 18px;
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: #333;
                }

                .project-subtitle {
                    color: #666;
                    font-size: 14px;
                    margin-bottom: 15px;
                    line-height: 1.4;
                }

                .project-meta {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    .category {
                        background: #e3f2fd;
                        color: #1f8cc6;
                        padding: 4px 12px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: 500;
                    }


                }
            }
        }
    }

    // Modal styles
    .project-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000000000000;
        padding: 20px;

        .modal-content {
            background: white;
            border-radius: 12px;
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;

            .close-btn {
                position: absolute;
                top: 20px;
                right: 20px;
                background: none;
                border: none;
                font-size: 30px;
                cursor: pointer;
                color: #666;
                z-index: 10;

                &:hover {
                    color: #333;
                }
            }

            .modal-body {
                padding: 40px;

                .project-header {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                    margin-bottom: 40px;

                    .project-hero-image {
                        width: 100%;
                        height: 300px;
                        object-fit: cover;
                        border-radius: 8px;
                    }

                    .project-header-info {
                        h2 {
                            font-size: 28px;
                            font-weight: 600;
                            margin-bottom: 10px;
                            color: #333;
                        }

                        .project-subtitle {
                            font-size: 16px;
                            color: #666;
                            margin-bottom: 20px;
                        }

                        .project-meta-detail {
                            .meta-item {
                                margin-bottom: 10px;
                                font-size: 14px;

                                strong {
                                    color: #333;
                                }
                            }
                        }
                    }
                }

                .project-description,
                .project-technologies,
                .project-results,
                .project-gallery {
                    margin-bottom: 30px;

                    h3 {
                        font-size: 20px;
                        font-weight: 600;
                        margin-bottom: 15px;
                        color: #333;
                    }
                }

                .tech-tags {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;

                    .tech-tag {
                        background: #f0f0f0;
                        color: #333;
                        padding: 8px 16px;
                        border-radius: 20px;
                        font-size: 14px;
                        font-weight: 500;
                    }
                }

                .results-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;

                    .result-item {
                        background: #f8f9fa;
                        padding: 20px;
                        border-radius: 8px;
                        text-align: center;

                        .result-label {
                            display: block;
                            font-size: 14px;
                            color: #666;
                            margin-bottom: 8px;
                        }

                        .result-value {
                            display: block;
                            font-size: 24px;
                            font-weight: 600;
                            color: #1f8cc6;
                        }
                    }
                }

                .gallery-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;

                    .gallery-item {
                        img, video {
                            width: 100%;
                            height: 200px;
                            object-fit: cover;
                            border-radius: 8px;
                        }
                    }
                }
            }
        }
    }
}

// Dark theme styles
body.dark-theme .portfolio-section {
    background: transparent;

    .portfolio-filters {
        .filter-btn {
            color: #ccc;

            &:hover {
                color: #ffffff;
            }

            &.active {
                color: #ffffff;
                font-weight: 600;

                &::after {
                    background: #f08908;
                }
            }
        }
    }

    .portfolio-grid {
        .portfolio-item {
            background: #1f2122;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

            &:hover {
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
            }

            .project-info {
                h3 {
                    color: #ffffff;
                }

                .project-subtitle {
                    color: #ccc;
                }

                .project-meta {
                    .category {
                        background: #2b2a2a;
                        color: #f08908;
                    }


                }
            }

            .project-overlay {
                background: rgba(31, 140, 198, 0.9);

                .overlay-content {
                    .project-tags {
                        .tag {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        }
    }

    .project-modal {
        .modal-content {
            background: #1f2122;
            color: #ffffff;

            .close-btn {
                color: #ccc;

                &:hover {
                    color: #ffffff;
                }
            }

            .modal-body {
                .project-header {
                    .project-header-info {
                        h2 {
                            color: #ffffff;
                        }

                        .project-subtitle {
                            color: #ccc;
                        }

                        .project-meta-detail {
                            .meta-item {
                                color: #ccc;

                                strong {
                                    color: #ffffff;
                                }
                            }
                        }
                    }
                }

                .project-description,
                .project-technologies,
                .project-results,
                .project-gallery {
                    h3 {
                        color: #ffffff;
                    }
                }

                .tech-tags {
                    .tech-tag {
                        background: #2b2a2a;
                        color: #ffffff;
                    }
                }

                .results-grid {
                    .result-item {
                        background: #2b2a2a;

                        .result-label {
                            color: #ccc;
                        }

                        .result-value {
                            color: #f08908;
                        }
                    }
                }
            }
        }
    }
}

// Responsive design
@media (max-width: 768px) {
    .portfolio-section {
        .loading-state,
        .error-state {
            p {
                color: #ccc;
            }
        }

        .retry-btn {
            background: #1f8cc6;
            
            &:hover {
                background: #1673a3;
            }
        }

        .portfolio-filters {
            gap: 15px;

            .filter-btn {
                padding: 8px 16px;
                font-size: 14px;
            }
        }

       
        .project-modal {
            .modal-content {
                .modal-body {
                    padding: 20px;

                    .project-header {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                }
            }
        }
    }
}
</style> 